import { styled } from 'styled-components';
import { Link, matchPath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { isDefined, Sentry } from '@idk-web/core-utils';
import {
  Styling,
  hostname,
  useBreakPoint,
  HamburgerButton,
  ResponsiveImage,
  Divider,
  Box,
  useDialogs,
  ErrorDialog,
  MobileNavDrawer,
  HeaderNavigation,
  MobileProfileMenu,
  ProfileMenu,
} from '@idk-web/core-ui';
import { translateError } from '@/utils/error';
import {
  availablePortalCompanies,
  portalLoginStatus,
  portalUser,
} from '@/state/portalAuth';
import { useCompany } from '@/hooks/portal/useCompany';
import { usePortalLogin } from '@/hooks/portal/login/usePortalLogin';
import routes from '@/components/portal/routes';
import ChangeCompanyDialog from '@/components/portal/header/ChangeCompanyDialog';
import LanguageSelect from '@/components/common/input/LanguageSelect';
import LogoutDialog from '@/components/common/dialog/LogoutDialog';
import idkollen from '@/assets/svg/idkollen.svg';
import identisure from '@/assets/svg/identisure.svg';

const Container = styled.header`
  position: relative;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  min-height: 80px;
  z-index: ${Styling.zIndex('header')};
  box-shadow: ${Styling.shadow('header')};
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  padding: 0 ${Styling.spacing(8)};
  @media (max-width: ${Styling.breakpoint('lg')}px) {
    padding: 0 ${Styling.spacing(4)};
  }
  @media (max-width: ${Styling.breakpoint('sm')}px) {
    padding: 0 ${Styling.spacing(2)};
  }
`;

const Section = styled(Box).attrs(({ theme }) => ({
  alignX: 'center',
  alignY: 'center',
  spacing: theme.spacing[2],
}))``;

const CenterSection = styled(Section)`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const HeaderText = styled(Box).attrs({
  alignX: 'center',
  alignY: 'center',
})`
  ${Styling.typography('h2')};
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.palette.secondary.main};
`;

const Logo = styled(ResponsiveImage).attrs({
  alt: 'Logo', // TODO translate
})`
  max-width: 160px;
  max-height: 120px;
  padding: ${Styling.spacing(2)} 0;
`;

const ShortDivider = styled(Divider)`
  height: 16px;
`;

const MyProfileText = styled.div`
  color: #458feb;
  font-weight: bold;
`;

const LogoutText = styled.div`
  color: ${({ theme }) => theme.palette.primary.main};
  font-weight: bold;
`;

const Header: FC = () => {
  const { t } = useTranslation();
  const dialog = useDialogs();
  const history = useHistory();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const breakpoint = useBreakPoint('xl');
  const auth = usePortalLogin();
  const company = useCompany();
  const loginStatus = useAtomValue(portalLoginStatus);
  const user = useAtomValue(portalUser);
  const companies = useAtomValue(availablePortalCompanies);
  const isLoggedIn = loginStatus === 'AUTHENTICATED';
  const paths = useMemo(() => {
    const paths = [
      { href: routes.teleid, title: t('portal.header.nav.teleid') },
      { href: routes.telesign, title: t('portal.header.nav.telesign') },
      { href: routes.safemail, title: t('portal.header.nav.safemail') },
    ];

    if (company?.isAdmin) {
      paths.push({ href: routes.admin, title: t('portal.header.nav.admin') });
    }

    return paths;
  }, [company, t]);
  const currentPath = useMemo(() => {
    return paths.find((path) =>
      matchPath(location.pathname, { path: path.href }),
    );
  }, [location, paths]);
  const logo = useMemo(() => {
    if (isLoggedIn && company?.companyLogoUrl) {
      return company.companyLogoUrl;
    }

    switch (hostname.tld) {
      case 'se':
        return idkollen;
      default:
        return identisure;
    }
  }, [isLoggedIn, company]);

  useEffect(() => {
    if (breakpoint.above) {
      setMobileMenuOpen(false);
    }
  }, [breakpoint]);

  const handleChangeCompany = (companyId: number) => {
    const handleOk = async () => {
      try {
        await company.change(companyId);
        history.push(routes.home);
      } catch (e) {
        Sentry.reportError('Failed to change company', { error: e });
        dialog.open((props) => (
          <ErrorDialog {...props} text={translateError(t, e)} />
        ));
      }
    };

    dialog.open((props) => <ChangeCompanyDialog {...props} onOk={handleOk} />);
  };

  const handleGoToProfile = () => history.push(routes.profile);

  const handleLogout = () => {
    const handleOk = async () => auth.logout();

    dialog.open((props) => <LogoutDialog {...props} onOk={handleOk} />);
  };

  const toggleMobileMenu = () => setMobileMenuOpen((open) => !open);

  const menuItems = [
    ...companies.map((company) => ({
      content: company.name,
      onClick: () => handleChangeCompany(company.id),
    })),
    companies.length > 0 ? ('divider' as const) : undefined,
    {
      content: (
        <MyProfileText>{t('portal.header.menu.profile.item')}</MyProfileText>
      ),
      onClick: handleGoToProfile,
    },
    'divider' as const,
    {
      content: <LogoutText>{t('portal.header.menu.log_out.item')}</LogoutText>,
      onClick: handleLogout,
    },
  ].filter(isDefined);

  return (
    <>
      {isLoggedIn && (
        <MobileNavDrawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          items={paths}
          logo={logo}
        />
      )}
      <Container>
        {breakpoint.below && isLoggedIn ? (
          <Section>
            <HamburgerButton onClick={toggleMobileMenu} />
          </Section>
        ) : (
          <Link to={routes.home}>
            <Logo src={logo} />
          </Link>
        )}
        <CenterSection>
          {isLoggedIn && breakpoint.above && <HeaderNavigation paths={paths} />}
          {isLoggedIn && breakpoint.below && (
            <HeaderText>{currentPath?.title}</HeaderText>
          )}
        </CenterSection>
        <Section>
          <LanguageSelect />
          {isLoggedIn && (
            <>
              <ShortDivider orientation="vertical" />
              {breakpoint.below ? (
                <MobileProfileMenu items={menuItems} />
              ) : (
                <ProfileMenu
                  username={user?.displayName ?? user?.username ?? 'Anonymous'}
                  items={menuItems}
                />
              )}
            </>
          )}
        </Section>
      </Container>
    </>
  );
};

export default Header;
